
import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useTranslation } from 'react-i18next';
import { useIsMobile } from '@/hooks/use-mobile';

interface AiStatusIndicatorProps {
  isVisible: boolean;
  isLoading: boolean;
  topic: 'guide' | 'performance' | 'trading_strategy' | 'advisor';
}

export const AiStatusIndicator: React.FC<AiStatusIndicatorProps> = ({ 
  isVisible, 
  isLoading,
  topic
}) => {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  
  const getAccentColor = () => {
    switch (topic) {
      case 'guide':
        return 'botscheme-royal-purple';
      case 'performance':
        return 'botscheme-ocean-blue';
      case 'trading_strategy':
        return 'botscheme-emerald-green';
      case 'advisor':
        return 'botscheme-amber-gold';
      default:
        return 'primary';
    }
  };
  
  const accentColor = getAccentColor();
  
  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          className={cn(
            "fixed z-50",
            isMobile ? "bottom-[130px] right-4" : "bottom-[90px] right-6"
          )}
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.8 }}
          transition={{ duration: 0.2 }}
        >
          <div className={cn(
            "flex items-center gap-2 px-3 py-1.5 rounded-full",
            "bg-background/80 backdrop-blur-md border border-white/10",
            "shadow-lg"
          )}>
            <Loader2 className={`h-3.5 w-3.5 text-${accentColor} animate-spin`} />
            <span className="text-xs text-muted-foreground">
              {t('ai_assistant.thinking')}
            </span>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
